import React, { useState } from 'react';

import {
  FloatingPortal,
  flip,
  offset,
  shift,
  useClientPoint,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';

import { cn } from '../../lib/utils';

// The canonical tooltip bubble surface — also used by programmatically-opened
// "Copied" confirmations that can't ride Tooltip's hover trigger.
export const TOOLTIP_BUBBLE_CLASS =
  'rounded-sm bg-gray-950/70 px-2 pt-1 pb-[5px] font-sans text-[13px] leading-5 font-medium text-white shadow-[0px_4px_14px_0px_rgba(0,0,0,0.25)] backdrop-blur-sm dark:bg-neutral-100 dark:text-neutral-900';

export const Tooltip = ({ children, label, placement = 'top', className = '', tooltipClassName = '', followCursor = true }) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    // Needed (padding: x) so the tooltip, when triggered by a channel in the sidebar, wasn't flush against the left side of the viewport
    middleware: [offset(6), flip(), shift({ padding: 8 })],
  });

  const hover = useHover(context, { delay: { open: 300, close: 0 } });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });
  // Anchor horizontally at the cursor instead of the target's center — on wide targets
  // a centered tooltip sits far from (or on top of) what the user is pointing at.
  // Vertical placement still comes from `placement`, and keyboard focus (no cursor)
  // falls back to the target rect. Side placements (left/right) pass followCursor=false
  // so the bubble anchors to the target's edge instead of the pointer.
  const clientPoint = useClientPoint(context, { axis: 'x', enabled: followCursor });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role, clientPoint]);

  // Scale up out of the anchor's edge on open, quick fade on close.
  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: { open: 180, close: 100 },
    initial: { opacity: 0, transform: 'scale(0.85)' },
    common: ({ side }) => ({
      transformOrigin: { top: 'bottom', bottom: 'top', left: 'right', right: 'left' }[side],
      transitionTimingFunction: 'var(--ease-fluid)',
    }),
  });

  return (
    <>
      {/* Tooltip targets are interactive by definition, so the wrapper opts out of
          window dragging and shows the pointer cursor itself. */}
      <div ref={refs.setReference} {...getReferenceProps()} className={cn('cursor-pointer', className)} data-app-region="none">
        {children}
      </div>
      {isMounted && (
        <FloatingPortal>
          {/* Position and animation transforms live on separate elements so they don't clobber each other. */}
          <div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()} className="z-(--stack-tooltip)">
            <div style={transitionStyles} className={cn(TOOLTIP_BUBBLE_CLASS, tooltipClassName)}>
              {label}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
